import { useMutation } from "@apollo/client";
import { Editor } from "@tiptap/core";
import { KeyboardEvent, useEffect, useRef, useState } from "react";
import { ArtifactType } from "types/graphql-schema";

import ArtifactIcon from "@apps/artifact/components/artifact-icon";
import Loading from "@components/loading/loading";

import createOrUpdateArtifactMutation from "./graphql/create-or-update-artifact-mutation";

const ActionItemQuickCreate = ({
  node,
  extension,
  updateAttributes,
  deleteNode,
  editor,
  getPos,
}: {
  node: any;
  extension: any;
  updateAttributes: (attributes: any) => void;
  deleteNode: () => void;
  editor: Editor;
  getPos: () => number | undefined;
}) => {
  const [title, setTitle] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  // Prevent blur saving twice when Enter fires save then the input unmounts.
  const committedRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // ProseMirror re-focuses its editor DOM after the input rule transaction,
    // so schedule our focus after that to win the race.
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, []);

  const [createOrUpdateArtifact, { loading }] = useMutation(
    createOrUpdateArtifactMutation,
  );

  const focusAfterNode = () => {
    try {
      const nodePos = getPos();
      if (nodePos === undefined) {
        editor.chain().focus().run();
        return;
      }
      editor
        .chain()
        .focus(nodePos + node.nodeSize)
        .run();
    } catch {
      editor.chain().focus().run();
    }
  };

  const commit = (value: string) => {
    if (committedRef.current) return;
    const trimmed = value.trim();
    if (!trimmed) {
      committedRef.current = true;
      deleteNode();
      return;
    }
    committedRef.current = true;
    createOrUpdateArtifact({
      variables: {
        artifactType: ArtifactType.ActionItem,
        title: trimmed,
        uuid: node.attrs.uuid,
        meetingId: extension.options.meetingId ?? undefined,
        relatedTopicId: extension.options.topicId ?? undefined,
        organizationId: extension.options.organizationId ?? undefined,
        relatedArtifactId: extension.options.relatedArtifactId ?? undefined,
      },
      onCompleted: (data) => {
        const artifact = data?.createOrUpdateArtifact?.artifact;
        if (artifact?.id) {
          updateAttributes({ id: artifact.id, quickCreate: null });
          focusAfterNode();
        }
      },
      onError: (err) => {
        committedRef.current = false;
        setError(err.message);
      },
    });
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commit(title);
    } else if (e.key === "Escape") {
      e.preventDefault();
      committedRef.current = true;
      deleteNode();
    }
  };

  const handleBlur = () => {
    if (loading) return;
    commit(title);
  };

  return (
    <div
      className="flex flex-1 items-center gap-2 min-w-0"
      contentEditable={false}
    >
      <div className="mt-1 shrink-0">
        <ArtifactIcon
          artifact={{ artifactType: ArtifactType.ActionItem }}
          size="5"
        />
      </div>
      <input
        ref={inputRef}
        aria-label="New action item title"
        autoFocus
        value={title}
        placeholder="Action item…"
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        disabled={loading}
        className="flex-1 min-w-0 bg-transparent outline-0 text-gray-800 font-medium py-0.5 disabled:opacity-50"
      />
      {loading && <Loading mini size="4" className="shrink-0" />}
      {error && (
        <span className="text-red-600 text-xs shrink-0" role="alert">
          {error}
        </span>
      )}
    </div>
  );
};

export default ActionItemQuickCreate;
